import React, { useState, useEffect } from 'react';
import { SEV_COLOR } from './Dashboard';

function relTime(alertsAt) {
  const diffMs = new Date(alertsAt).getTime() - Date.now();
  if (diffMs <= 0) return 'now';
  const mins = Math.floor(diffMs / 60000);
  const hrs = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (mins < 60) return `in ${mins}m`;
  if (hrs < 24) return `in ${hrs}h`;
  return `in ${days}d`;
}

export default function RaspAIAllView({ API, projects, onSelectProject }) {
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('expertnet_token');
    fetch(`${API}/dashboard/rasp-ai/queue`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(data => { setQueue(data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [API]);

  const openProject = (projectId) => {
    const proj = (projects || []).find(p => p.id === projectId);
    if (proj) onSelectProject(proj);
  };

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {loading && (
        <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading...</div>
      )}
      {!loading && error && (
        <div style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn't load the queue.</div>
      )}
      {!loading && !error && queue && (
        <>
          {/* Active card */}
          <div className="r-card" style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span className="r-eyebrow">Active</span>
              <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)', border: '1px solid var(--line)', borderRadius: 999, padding: '1px 8px' }}>
                {queue.active.length}
              </span>
            </div>
            {queue.active.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-4)' }}>Nothing active.</div>
            ) : (
              queue.active.map((item, idx) => {
                const sevColor = SEV_COLOR[item.severity] || 'var(--text-4)';
                const canNav = item.project_id != null;
                return (
                  <div
                    key={idx}
                    onClick={() => { if (canNav) openProject(item.project_id); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, minHeight: 40,
                      borderTop: '1px solid var(--line-soft)', padding: '4px 0',
                      cursor: canNav ? 'pointer' : 'default', background: 'transparent',
                    }}
                    onMouseEnter={e => { if (canNav) e.currentTarget.style.background = 'var(--row-hover)'; }}
                    onMouseLeave={e => { if (canNav) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span className="r-dot" style={{ color: sevColor, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.expert_name
                          ? <>{item.expert_name}<span style={{ color: 'var(--text-5)', margin: '0 4px' }}>·</span><span style={{ color: 'var(--text-3)' }}>{item.project_name}</span></>
                          : item.project_name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.action_label}
                      </div>
                    </div>
                    {typeof item.days_stale === 'number' && (
                      <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)', flexShrink: 0 }}>
                        {Math.floor(item.days_stale)}d silent
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Coming up card */}
          <div className="r-card" style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span className="r-eyebrow">Coming up</span>
              <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)', border: '1px solid var(--line)', borderRadius: 999, padding: '1px 8px' }}>
                {queue.upcoming.length}
              </span>
            </div>
            {queue.upcoming.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-4)' }}>Nothing queued.</div>
            ) : (
              queue.upcoming.map((item, idx) => {
                const canNav = item.project_id != null;
                const localTime = new Date(item.alerts_at).toLocaleString();
                const isSnoozed = item.reason === 'snoozed';
                return (
                  <div
                    key={idx}
                    onClick={() => { if (canNav) openProject(item.project_id); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, minHeight: 40,
                      borderTop: '1px solid var(--line-soft)', padding: '4px 0',
                      cursor: canNav ? 'pointer' : 'default', background: 'transparent',
                    }}
                    onMouseEnter={e => { if (canNav) e.currentTarget.style.background = 'var(--row-hover)'; }}
                    onMouseLeave={e => { if (canNav) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{
                      fontFamily: 'var(--font-data)', fontSize: 10, fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '.08em',
                      padding: '2px 7px', borderRadius: 4, flexShrink: 0,
                      background: isSnoozed ? 'rgba(74,104,120,.35)' : 'rgba(45,212,191,.12)',
                      color: isSnoozed ? 'var(--text-3)' : 'var(--accent)',
                      border: isSnoozed ? '1px solid rgba(74,104,120,.5)' : '1px solid rgba(45,212,191,.3)',
                    }}>
                      {isSnoozed ? 'Snoozed' : 'Waiting'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.expert_name
                          ? <>{item.expert_name}<span style={{ color: 'var(--text-5)', margin: '0 4px' }}>·</span><span style={{ color: 'var(--text-3)' }}>{item.project_name}</span></>
                          : item.project_name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.action_label}
                      </div>
                    </div>
                    <span
                      title={localTime}
                      style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)', flexShrink: 0 }}
                    >
                      {relTime(item.alerts_at)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
